import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { images, eventInfo } from '../data/mock';
import { 
  Store, Building2, Wine, Check, Star, 
  Users, MapPin, Mail, Phone, User, Package, Send,
  CheckCircle, Crown, Ticket, Image as ImageIcon,
  Share2, Gift, Home, ArrowLeft, Loader2, UtensilsCrossed,
  FileText, ShieldCheck, ExternalLink, Calendar, Clock, AlertCircle,
  HandHeart, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_EMAIL = 'everything@8eight8.net';

// Vendor Form Component
const VendorForm = () => {
  const [formData, setFormData] = useState({
    vendorType: '',
    name: '',
    email: '',
    phone: '',
    productDescription: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.vendorType) {
      setError('Please select a vendor type (Artist, Food Vendor, or Product Seller) above.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/applications/vendor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendor_type: formData.vendorType,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          product_description: formData.productDescription
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit application');
      
      setSubmitted(true);
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('vendor_application_submit', { vendor_type: formData.vendorType });
      }
    } catch (err) {
      setError('Failed to submit. Please try again or email us directly at everything@8eight8.net.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 animate-fadeIn" data-testid="vendor-success">
        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h4 className="text-2xl font-bold text-white mb-2">Application Submitted!</h4>
        <p className="text-white/70">Thank you! We&apos;ll review your application and get back to you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="vendor-form">
      <div>
        <label className="text-white/80 text-sm mb-2 block">Vendor Type *</label>
        <div className="grid grid-cols-3 gap-3">
          {['Artist', 'Food Vendor', 'Product Seller'].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setFormData({ ...formData, vendorType: type })}
              data-testid={`vendor-type-${type.toLowerCase().replace(' ', '-')}`}
              className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                formData.vendorType === type
                  ? 'bg-pink-500 border-pink-500 text-white'
                  : 'bg-white/5 border-white/20 text-white/70 hover:border-pink-500/50'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="text"
            placeholder="Your Name / Business Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
            data-testid="vendor-name"
            required
          />
        </div>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
            data-testid="vendor-email"
            required
          />
        </div>
      </div>
      
      <div className="relative">
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
        <Input
          type="tel"
          placeholder="Phone Number"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
          data-testid="vendor-phone"
          required
        />
      </div>
      
      <div className="relative">
        <Package className="absolute left-3 top-3 w-5 h-5 text-white/50" />
        <Textarea
          placeholder="Describe your products/services..."
          value={formData.productDescription}
          onChange={(e) => setFormData({ ...formData, productDescription: e.target.value })}
          className="min-h-[100px] pl-10 pt-3 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl resize-none"
          data-testid="vendor-description"
          required
        />
      </div>
      
      {error && (
        <div className="flex items-start gap-2 bg-red-500/15 border border-red-400/50 rounded-xl px-4 py-3" data-testid="vendor-error">
          <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}
      
      <Button 
        type="submit"
        disabled={loading}
        data-testid="vendor-submit"
        className="w-full h-12 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {loading ? 'Submitting...' : 'Submit Vendor Application'}
      </Button>
    </form>
  );
};

// Business Sponsor Form Component
const BusinessSponsorForm = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    businessType: '',
    selectedTier: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.selectedTier) {
      setError('Please select a sponsor tier (Silver, Gold, Platinum, etc.) above.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/applications/business-sponsor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          business_type: formData.businessType,
          selected_tier: formData.selectedTier
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit application');
      
      setSubmitted(true);
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('sponsor_application_submit', { selected_tier: formData.selectedTier });
      }
    } catch (err) {
      setError('Failed to submit. Please try again or email us directly at everything@8eight8.net.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 animate-fadeIn" data-testid="business-success">
        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h4 className="text-2xl font-bold text-white mb-2">Application Submitted!</h4>
        <p className="text-white/70">Thank you! We&apos;ll review your sponsorship application and contact you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="business-form">
      <div>
        <label className="text-white/80 text-sm mb-2 block">Select Sponsor Tier *</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, selectedTier: 'Silver Sponsor - $1,500' })}
            data-testid="tier-silver"
            className={`p-3 rounded-xl border text-left transition-all ${
              formData.selectedTier === 'Silver Sponsor - $1,500'
                ? 'bg-gray-500/20 border-gray-400 text-white'
                : 'bg-white/5 border-white/20 text-white/70 hover:border-gray-400/50'
            }`}
          >
            <div className="font-bold text-sm">Silver Sponsor</div>
            <div className="text-xl font-black text-gray-300">$1,500</div>
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, selectedTier: 'Gold Sponsor - $5,000' })}
            data-testid="tier-gold"
            className={`p-3 rounded-xl border text-left transition-all ${
              formData.selectedTier === 'Gold Sponsor - $5,000'
                ? 'bg-yellow-500/20 border-yellow-500 text-white'
                : 'bg-white/5 border-white/20 text-white/70 hover:border-yellow-500/50'
            }`}
          >
            <div className="font-bold text-sm">Gold Sponsor</div>
            <div className="text-xl font-black text-yellow-400">$5,000</div>
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, selectedTier: 'Platinum Sponsor - $10,000' })}
            data-testid="tier-platinum"
            className={`p-3 rounded-xl border text-left transition-all ${
              formData.selectedTier === 'Platinum Sponsor - $10,000'
                ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500 text-white'
                : 'bg-white/5 border-white/20 text-white/70 hover:border-purple-500/50'
            }`}
          >
            <div className="font-bold flex items-center gap-1 text-sm">
              <Crown className="w-3 h-3 text-purple-400" />
              Platinum
            </div>
            <div className="text-xl font-black text-purple-400">$10,000</div>
          </button>
        </div>
        <p className="text-white/50 text-xs mt-2">Or contact us for Additional Sponsorship Opportunities</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="text"
            placeholder="Company Name"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
            data-testid="business-company"
            required
          />
        </div>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="text"
            placeholder="Contact Name"
            value={formData.contactName}
            onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
            data-testid="business-contact"
            required
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
            data-testid="business-email"
            required
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="tel"
            placeholder="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
            data-testid="business-phone"
            required
          />
        </div>
      </div>
      
      <div className="relative">
        <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
        <Input
          type="text"
          placeholder="Type of Business"
          value={formData.businessType}
          onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
          className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
          data-testid="business-type"
          required
        />
      </div>
      
      {error && (
        <div className="flex items-start gap-2 bg-red-500/15 border border-red-400/50 rounded-xl px-4 py-3" data-testid="business-error">
          <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}
      
      <Button 
        type="submit"
        disabled={loading}
        data-testid="business-submit"
        className="w-full h-12 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {loading ? 'Submitting...' : 'Submit Sponsor Application'}
      </Button>
    </form>
  );
};

// Beverage Sponsor Form Component
const BeverageSponsorForm = () => {
  const [formData, setFormData] = useState({
    brandName: '',
    contactName: '',
    email: '',
    phone: '',
    selectedTier: '',
    brandDescription: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.selectedTier) {
      setError('Please select an exhibitor tier (Exhibitor, Featured, Standard, or Sample) above.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/applications/beverage-sponsor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand_name: formData.brandName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          brand_description: `Tier: ${formData.selectedTier}\n\n${formData.brandDescription}`
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit application');
      
      setSubmitted(true);
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('beverage_application_submit', { selected_tier: formData.selectedTier });
      }
    } catch (err) {
      setError('Failed to submit. Please try again or email us directly at everything@8eight8.net.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 animate-fadeIn" data-testid="beverage-success">
        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
        <h4 className="text-2xl font-bold text-white mb-2">Application Submitted!</h4>
        <p className="text-white/70">Thank you! We&apos;ll review your exhibitor application and contact you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="beverage-form">
      <div>
        <label className="text-white/80 text-sm mb-2 block">Select Exhibitor Tier *</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { id: 'Exhibitor - $2,500', label: 'Exhibitor', price: '$2,500', color: 'amber' },
            { id: 'Featured Exhibitor - $1,200', label: 'Featured', price: '$1,200', color: 'orange' },
            { id: 'Standard Exhibitor - $700', label: 'Standard', price: '$700', color: 'pink' },
            { id: 'Sample Exhibitor - $450', label: 'Sample', price: '$450', color: 'gray' }
          ].map((tier) => (
            <button
              key={tier.id}
              type="button"
              onClick={() => setFormData({ ...formData, selectedTier: tier.id })}
              className={`p-3 rounded-xl border text-center transition-all ${
                formData.selectedTier === tier.id
                  ? `bg-${tier.color}-500/20 border-${tier.color}-500 text-white`
                  : 'bg-white/5 border-white/20 text-white/70 hover:border-amber-500/50'
              }`}
            >
              <div className="font-bold text-xs">{tier.label}</div>
              <div className={`text-lg font-black ${formData.selectedTier === tier.id ? `text-${tier.color}-400` : 'text-white'}`}>{tier.price}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="relative">
          <Wine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="text"
            placeholder="Brand Name"
            value={formData.brandName}
            onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-amber-500 rounded-xl"
            data-testid="beverage-brand"
            required
          />
        </div>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="text"
            placeholder="Contact Name"
            value={formData.contactName}
            onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-amber-500 rounded-xl"
            data-testid="beverage-contact"
            required
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-amber-500 rounded-xl"
            data-testid="beverage-email"
            required
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
          <Input
            type="tel"
            placeholder="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-amber-500 rounded-xl"
            data-testid="beverage-phone"
            required
          />
        </div>
      </div>
      
      <div className="relative">
        <Package className="absolute left-3 top-3 w-5 h-5 text-white/50" />
        <Textarea
          placeholder="Tell us about your brand and products..."
          value={formData.brandDescription}
          onChange={(e) => setFormData({ ...formData, brandDescription: e.target.value })}
          className="min-h-[100px] pl-10 pt-3 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-amber-500 rounded-xl resize-none"
          data-testid="beverage-description"
          required
        />
      </div>
      
      {error && (
        <div className="flex items-start gap-2 bg-red-500/15 border border-red-400/50 rounded-xl px-4 py-3" data-testid="beverage-error">
          <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}
      
      <Button 
        type="submit"
        disabled={loading}
        data-testid="beverage-submit"
        className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {loading ? 'Submitting...' : 'Submit Exhibitor Application'}
      </Button>
    </form>
  );
};

const VendorSponsorPage = () => {
  const [activeTab, setActiveTab] = useState('vendors');

  return (
    <div className="min-h-screen bg-gray-950" data-testid="vendor-sponsor-page">
      <Helmet>
        <title>Become a Vendor or Sponsor | Aloha Tequila & Arts Festival 2026</title>
        <meta name="description" content="Partner with Hawaii's premier tequila festival. Apply to become a vendor, business sponsor, or beverage exhibitor at the Aloha Tequila & Arts Festival." />
        <link rel="canonical" href="https://www.alohatequilafest.com/vendor-sponsor" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-500/10 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-pink-400 transition-colors mb-8"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-12">
            <img 
              src={images.logo} 
              alt="Aloha Tequila & Arts Festival" 
              className="h-32 md:h-40 mx-auto mb-6 object-contain"
            />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Become a Vendor or Sponsor
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Join Hawaii&apos;s premier tequila & arts festival! Partner with us for an unforgettable event.
            </p>
            <p className="text-pink-400 font-semibold mt-2">{eventInfo.date} • {eventInfo.location}</p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="tabs">
            {[
              { id: 'vendors', label: 'Vendors', icon: Store },
              { id: 'business', label: 'Business Sponsors', icon: Building2 },
              { id: 'beverage', label: 'Beverage Sponsors', icon: Wine },
              { id: 'volunteers', label: 'Volunteers', icon: HandHeart }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-pink-500 to-orange-500 text-white shadow-lg shadow-pink-500/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto">
            {/* Vendors Tab */}
            {activeTab === 'vendors' && (
              <div className="animate-fadeIn space-y-6" data-testid="vendors-content">
                {/* Food Vendor Section */}
                <Card className="bg-gradient-to-br from-orange-500/20 via-amber-500/10 to-transparent backdrop-blur-sm border-orange-500/30 p-5 sm:p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center">
                      <UtensilsCrossed className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white">Food & Restaurant Partners</h2>
                      <p className="text-white/60 text-sm">Mexican / Agave-Aligned Cuisine</p>
                    </div>
                  </div>

                  {/* Event Overview */}
                  <div className="mb-6 p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
                    <p className="text-white/80 text-sm leading-relaxed">
                      The Aloha Tequila & Arts Festival is a curated tequila tasting experience designed to support restaurant partners while delivering a high-quality guest experience. Food offerings complement the agave tasting experience. <strong className="text-orange-400">Limited to 6–8 restaurants.</strong>
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* What's Included */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                        <Gift className="w-5 h-5 text-orange-400" />
                        What&apos;s Included
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {[
                          '$750 guaranteed stipend',
                          'Participation in curated event (6-8 restaurants)',
                          'Exposure to ~1,000 attendees',
                          'Electricity access (if requested)',
                          'Centralized payment system'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-white/70">
                            <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Requirements */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-orange-400" />
                        Requirements
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {[
                          'Menu must be Mexican / agave-aligned',
                          'Maximum 1–2 menu items',
                          'Fast service format (1–2 min per order)',
                          'Must provide: Plateware, Flatware, Napkins, Tent'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-white/70">
                            <Check className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* DOH Compliance */}
                  <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20 mb-6">
                    <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-red-400" />
                      DOH Compliance (MANDATORY)
                    </h3>
                    <ul className="space-y-1 text-sm text-white/70 mb-3">
                      <li>• At least one certified food handler onsite</li>
                      <li>• Maintain proper food safety practices</li>
                      <li>• Obtain Special Event Permit</li>
                    </ul>
                    <a 
                      href="https://health.hawaii.gov/san/special-event-permit-applications/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-semibold transition-colors"
                    >
                      Apply for Permit Here
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <p className="text-red-300 text-xs mt-2 font-medium">
                      Application deadline: May 20, 2026
                    </p>
                  </div>

                  {/* Event Details */}
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="bg-white/10 px-3 py-1 rounded-full text-white/70">
                      <MapPin className="w-4 h-4 inline mr-1" />International Market Place
                    </span>
                    <span className="bg-white/10 px-3 py-1 rounded-full text-white/70">
                      <Calendar className="w-4 h-4 inline mr-1" />June 20, 2026
                    </span>
                    <span className="bg-white/10 px-3 py-1 rounded-full text-white/70">
                      <Clock className="w-4 h-4 inline mr-1" />5:00 PM – 9:00 PM
                    </span>
                  </div>

                  {/* Food Vendor Sign Up CTA */}
                  <a
                    href="https://forms.gle/3c6mLfDsJNCYR8mQ9"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="food-vendor-signup-link"
                    className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-6 py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.01] shadow-lg shadow-orange-500/20"
                  >
                    <UtensilsCrossed className="w-5 h-5" />
                    Food Vendor Sign Up
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Card>

                {/* Artists & Product Sellers Section */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center">
                      <Store className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white">Artists & Product Sellers</h2>
                      <p className="text-white/60 text-sm">Artisans, Crafts & Local Products</p>
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 bg-pink-500/10 rounded-xl border border-pink-500/20">
                    <p className="text-white/80 text-sm">
                      <strong className="text-pink-400">Looking for:</strong> Local artists, artisans, and product sellers to showcase at our festival. 
                      Perfect for handmade crafts, art, jewelry, clothing, and more!
                    </p>
                  </div>

                  <VendorForm />
                </Card>
              </div>
            )}

            {/* Business Sponsors Tab */}
            {activeTab === 'business' && (
              <div className="animate-fadeIn space-y-6" data-testid="business-content">
                {/* Main Sponsor Tiers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Silver Sponsor $1,500 */}
                  <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-6 rounded-2xl">
                    <div className="text-center mb-4 pb-4 border-b border-white/10">
                      <span className="text-xs text-gray-400 font-semibold tracking-wider">SILVER SPONSOR</span>
                      <div className="text-3xl font-black text-white mt-1">$1,500</div>
                    </div>
                    <ul className="space-y-2 text-xs sm:text-sm">
                      {[
                        'Logo on alohatequilafest.com website',
                        'Logo on event signage',
                        'Festival social media recognition',
                        '10x10 promotional booth space',
                        'Stage recognition during event',
                        'Display company banner',
                        'Radio mentions during promotions'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/70">
                          <Check className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white font-semibold text-sm flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-gray-400" />
                        10 VIP Tickets <span className="text-white/50 text-xs">(Value $1,200)</span>
                      </p>
                    </div>
                  </Card>

                  {/* Gold Sponsor $5,000 */}
                  <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 backdrop-blur-sm border-yellow-500/30 p-4 sm:p-6 rounded-2xl relative">
                    <div className="absolute top-2 right-2">
                      <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">POPULAR</span>
                    </div>
                    <div className="text-center mb-4 pb-4 border-b border-yellow-500/20">
                      <span className="text-xs text-yellow-400 font-semibold tracking-wider">GOLD SPONSOR</span>
                      <div className="text-3xl font-black text-white mt-1">$5,000</div>
                    </div>
                    <ul className="space-y-2 text-xs sm:text-sm">
                      {[
                        'Logo on festival website',
                        'Logo on event signage',
                        'Festival social media recognition',
                        '10x10 promotional booth space',
                        'Stage recognition during event',
                        'Display company banner',
                        'Radio mentions during promotions'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/70">
                          <Check className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-yellow-500/20">
                      <p className="text-white font-semibold text-sm flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-yellow-400" />
                        15 VIP Tickets <span className="text-white/50 text-xs">(Value $1,800)</span>
                      </p>
                    </div>
                  </Card>

                  {/* Platinum Sponsor $10,000 */}
                  <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm border-purple-500/30 p-4 sm:p-6 rounded-2xl relative">
                    <div className="absolute top-2 right-2">
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">PREMIUM</span>
                    </div>
                    <div className="text-center mb-4 pb-4 border-b border-purple-500/20">
                      <span className="text-xs text-purple-400 font-semibold tracking-wider flex items-center justify-center gap-1">
                        <Crown className="w-3 h-3" />
                        PLATINUM SPONSOR
                      </span>
                      <div className="text-3xl font-black text-white mt-1">$10,000</div>
                    </div>
                    <p className="text-purple-300 text-xs mb-3 font-medium">All Gold benefits plus:</p>
                    <ul className="space-y-2 text-xs sm:text-sm">
                      {[
                        'Premier sponsor recognition',
                        'Premium booth location',
                        'Homepage logo placement',
                        'VIP lounge recognition & banner',
                        'Present People\'s Choice Award',
                        'Multiple social media promotions',
                        'Radio & TV sponsor spotlight',
                        'Hotel at Hilton Waikiki (Value $500)'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-white/70">
                          <Check className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-purple-500/20">
                      <p className="text-white font-semibold text-sm flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-purple-400" />
                        30 VIP Tickets <span className="text-white/50 text-xs">(Value $3,600)</span>
                      </p>
                    </div>
                  </Card>
                </div>

                {/* Additional Sponsorship Opportunities */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-6 rounded-2xl">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-pink-400" />
                    Additional Sponsorship Opportunities
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { name: 'Stage Sponsor', price: '$1,500' },
                      { name: 'Tequila Tasting Glass', price: '$2,500' },
                      { name: 'People\'s Choice Award', price: '$1,000' },
                      { name: 'Lanyard Sponsor', price: '$1,000' },
                      { name: 'Photo Booth Sponsor', price: '$1,500' },
                      { name: 'VIP Lounge Sponsor', price: '$1,500' }
                    ].map((item, i) => (
                      <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center hover:border-pink-500/30 transition-colors">
                        <p className="text-white/80 text-xs sm:text-sm font-medium">{item.name}</p>
                        <p className="text-pink-400 font-bold text-sm sm:text-base">{item.price}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/50 text-xs mt-3 text-center">*Additional benefits available. Contact us for details.</p>
                </Card>

                <p className="text-white/60 text-xs text-center">
                  VIP tickets may be used for client entertainment, employee incentives, or promotional giveaways.
                </p>

                {/* Application Form */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Business Sponsor Application</h2>
                      <p className="text-white/60">Support the festival & get premium exposure</p>
                    </div>
                  </div>
                  <BusinessSponsorForm />
                </Card>
              </div>
            )}

            {/* Beverage Sponsors Tab */}
            {activeTab === 'beverage' && (
              <div className="animate-fadeIn space-y-6" data-testid="beverage-content">
                {/* Exhibitor Tiers Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  
                  {/* Exhibitor - $2,500 */}
                  <Card className="bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent backdrop-blur-sm border-amber-500/30 p-4 sm:p-6 rounded-2xl relative">
                    <div className="absolute top-2 right-2">
                      <span className="bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">TOP TIER</span>
                    </div>
                    <div className="mb-4 pb-3 border-b border-amber-500/20">
                      <span className="text-xs text-amber-400 font-semibold tracking-wider">EXHIBITOR</span>
                      <div className="text-3xl font-black text-white">$2,500</div>
                      <p className="text-amber-300 text-sm font-medium mt-1">15 GA + 5 VIP Tickets</p>
                    </div>
                    <ul className="space-y-1.5 text-xs text-white/70">
                      {[
                        'Logo on all event signage & step-and-repeat',
                        '8x PA/Stage announcements',
                        'Featured Interview + Content Reel',
                        'Featured in Order Book',
                        'Name in event title',
                        'Key Location placement',
                        'Attendee list & event data access',
                        '6 social media posts + 2 stories',
                        'Logo on all promo materials',
                        'Website logo placement',
                        'Branded discount code'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-3 border-t border-amber-500/20">
                      <p className="text-white text-xs font-semibold mb-1">Exhibitor Space:</p>
                      <p className="text-white/60 text-xs">3x 6-foot sections, 3 tables, 2 chairs, ice/trash/porter service</p>
                      <p className="text-amber-400 text-xs mt-2 font-medium">Bonus: Pre-sell through Tamura&apos;s & Fujioka&apos;s</p>
                    </div>
                  </Card>

                  {/* Featured Exhibitor - $1,200 */}
                  <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-6 rounded-2xl">
                    <div className="mb-4 pb-3 border-b border-white/10">
                      <span className="text-xs text-orange-400 font-semibold tracking-wider">FEATURED EXHIBITOR</span>
                      <div className="text-3xl font-black text-white">$1,200</div>
                      <p className="text-orange-300 text-sm font-medium mt-1">10 GA + 5 VIP Tickets</p>
                    </div>
                    <ul className="space-y-1.5 text-xs text-white/70">
                      {[
                        'Logo on event signage & step-and-repeat',
                        '4x PA/Stage announcements',
                        'Featured Interview + Content Reel',
                        'Featured in Order Book',
                        'Name in event title',
                        'Key Location placement',
                        'Attendee list & event data access',
                        '3 social media posts + 1 story',
                        'Logo on promo materials',
                        'Website logo placement',
                        'Branded discount code'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-3 h-3 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <p className="text-white text-xs font-semibold mb-1">Exhibitor Space:</p>
                      <p className="text-white/60 text-xs">2x 6-foot sections, 2 tables, 2 chairs, ice/trash/porter service</p>
                      <p className="text-orange-400 text-xs mt-2 font-medium">Bonus: Pre-sell through Tamura&apos;s & Fujioka&apos;s</p>
                    </div>
                  </Card>

                  {/* Standard Exhibitor - $700 */}
                  <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-6 rounded-2xl">
                    <div className="mb-4 pb-3 border-b border-white/10">
                      <span className="text-xs text-pink-400 font-semibold tracking-wider">STANDARD EXHIBITOR</span>
                      <div className="text-3xl font-black text-white">$700</div>
                      <p className="text-pink-300 text-sm font-medium mt-1">6 GA + 3 VIP Tickets</p>
                    </div>
                    <ul className="space-y-1.5 text-xs text-white/70">
                      {[
                        '2x PA/Stage announcements',
                        'Featured Interview + Content Reel',
                        'Featured in Order Book',
                        'Name in event title',
                        'Key Location placement',
                        'Attendee list & event data access',
                        '1 social media post + 1 story',
                        'Logo on promo materials',
                        'Website logo placement',
                        'Branded discount code'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-3 h-3 text-pink-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <p className="text-white text-xs font-semibold mb-1">Exhibitor Space:</p>
                      <p className="text-white/60 text-xs">1x 6-foot section, 1 table, 2 chairs, ice/trash/porter service</p>
                      <p className="text-pink-400 text-xs mt-2 font-medium">Bonus: Pre-sell through Tamura&apos;s & Fujioka&apos;s</p>
                    </div>
                  </Card>

                  {/* Sample Exhibitor - $450 */}
                  <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-6 rounded-2xl">
                    <div className="mb-4 pb-3 border-b border-white/10">
                      <span className="text-xs text-gray-400 font-semibold tracking-wider">SAMPLE EXHIBITOR</span>
                      <div className="text-3xl font-black text-white">$450</div>
                      <p className="text-gray-300 text-sm font-medium mt-1">4 GA + 2 VIP Tickets</p>
                    </div>
                    <ul className="space-y-1.5 text-xs text-white/70">
                      {[
                        '1x PA/Stage announcement',
                        'Featured in Order Book',
                        'Logo on promo materials',
                        '1 dedicated social media post',
                        'Website logo placement',
                        'Branded discount code'
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <p className="text-white text-xs font-semibold mb-1">Exhibitor Space:</p>
                      <p className="text-white/60 text-xs">1x 6-foot table, 1 table, 2 chairs, ice/trash/porter service</p>
                      <p className="text-gray-400 text-xs mt-2 font-medium">Bonus: Pre-sell through Tamura&apos;s & Fujioka&apos;s</p>
                    </div>
                  </Card>
                </div>

                {/* Sponsor Provides Note */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
                  <p className="text-amber-300 text-sm">
                    <strong>Sponsor provides:</strong> Tequila, cups, and point of sale materials
                  </p>
                </div>

                {/* Application Form */}
                <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-6 sm:p-8 rounded-3xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
                      <Wine className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white">Exhibitor Application</h2>
                      <p className="text-white/60 text-sm">Showcase your tequila, mezcal, or agave spirits</p>
                    </div>
                  </div>
                  <BeverageSponsorForm />
                </Card>
              </div>
            )}

            {/* Volunteers Tab */}
            {activeTab === 'volunteers' && (
              <div className="animate-fadeIn space-y-6" data-testid="volunteers-content">
                <Card className="bg-gradient-to-br from-pink-500/20 via-orange-500/10 to-amber-500/10 backdrop-blur-sm border-pink-500/30 p-6 sm:p-10 rounded-3xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/40">
                      <HandHeart className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl sm:text-3xl font-black text-white">Volunteers Needed!</h2>
                      <p className="text-pink-300 text-sm font-semibold">Join the ohana behind Hawaii&apos;s premier tequila festival</p>
                    </div>
                  </div>

                  <div className="mb-6 p-5 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                      We&apos;re building a team of <strong className="text-pink-300">passionate volunteers</strong> to help bring the 1st Annual Aloha Tequila &amp; Arts Festival to life on <strong className="text-amber-300">Saturday, June 20, 2026</strong>. Every shift directly supports the <strong className="text-orange-300">Aloha Cancer Project</strong>.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-6">
                    <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                      <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-pink-400" />
                        What You Get
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {[
                          'Free festival entry on your off-shift hours',
                          'Exclusive volunteer t-shirt & swag bag',
                          'Meal voucher + 2 drink tickets',
                          'Behind-the-scenes access to brand reps',
                          'Letter of service for school / community hours',
                          'Aloha. Lots of it.'
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2 text-white/75">
                            <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                      <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5 text-orange-400" />
                        Roles We&apos;re Hiring
                      </h3>
                      <ul className="space-y-2 text-sm">
                        {[
                          'Check-in & ID verification (21+)',
                          'Pour station support (no bartending required)',
                          'Wristband & ticket scanning',
                          'Vendor & brand ambassador runners',
                          'Setup crew (afternoon) & breakdown crew (evening)',
                          'Social media / photo capture team'
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2 text-white/75">
                            <Check className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-pink-500/10 rounded-xl p-5 border border-pink-500/20 mb-6">
                    <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-pink-400" />
                      Requirements
                    </h3>
                    <ul className="grid sm:grid-cols-2 gap-2 text-sm text-white/75">
                      <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" /><span>Must be 21+ with valid government-issued ID</span></li>
                      <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" /><span>Available for a 4–5 hr shift on June 20, 2026</span></li>
                      <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" /><span>Friendly, reliable, on-time</span></li>
                      <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" /><span>Brief 30-min virtual orientation week-of</span></li>
                    </ul>
                  </div>

                  <div className="text-center">
                    <a
                      href={`mailto:${CONTACT_EMAIL}?subject=Volunteer%20Application%20-%20Aloha%20Tequila%20%26%20Arts%20Festival%202026&body=Hi%20Aloha%20Tequila%20Fest%20team%2C%0A%0AI%27d%20like%20to%20volunteer%20for%20the%20festival%20on%20June%2020%2C%202026.%0A%0AName%3A%0APhone%3A%0AAge%20%2821%2B%3F%29%3A%0APreferred%20role%20%28check-in%2Fpour%20support%2Fsetup%2Fetc.%29%3A%0AAvailability%20window%20on%20June%2020%3A%0AAny%20experience%20at%20festivals%20or%20events%3F%0A%0AMahalo%21`}
                      data-testid="volunteer-apply-cta"
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-pink-500 via-orange-500 to-amber-500 hover:from-pink-600 hover:via-orange-600 hover:to-amber-600 text-white font-black text-base sm:text-lg px-8 py-4 rounded-full shadow-xl shadow-pink-500/30 transition-all hover:scale-105"
                    >
                      <HandHeart className="w-6 h-6" />
                      Apply to Volunteer
                      <Send className="w-5 h-5" />
                    </a>
                    <p className="text-white/50 text-xs mt-4">
                      Or email <a href={`mailto:${CONTACT_EMAIL}`} className="text-pink-400 underline">{CONTACT_EMAIL}</a> with the subject line <em>&ldquo;Volunteer Application&rdquo;</em>
                    </p>
                  </div>
                </Card>
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="text-center mt-12">
            <p className="text-white/60 mb-2">Questions? Contact us directly:</p>
            <a 
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-2 text-pink-400 font-semibold hover:text-pink-300 transition-colors"
              data-testid="contact-email"
            >
              <Mail className="w-5 h-5" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VendorSponsorPage;
